import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import isRequiredIf from 'react-proptype-conditional-require';
import { connect } from 'react-redux';
import Router from 'next/router';
import queryString from 'query-string';
import once from 'lodash/once';
import Button from '@deliveroo/consumer-component-library/components/Button';
import LocationSearch from '@deliveroo/consumer-component-library/components/LocationSearch';
import LocationMap from '@deliveroo/consumer-component-library/components/LocationMap';
import LocationModal from '@deliveroo/consumer-component-library/components/LocationModal';
import { shortAddress, validatePostcode } from '../../utils/address';
import VoteAreaContainer from '../VoteAreaContainer';
import UndeliverableContainer from '../UndeliverableContainer';
import { showVoteAreaModal } from '../../actions/voteAreaActions';
import { hasVariant } from '../../selectors/configSelectors';
import loadGoogleMapsApi from '../../utils/loadGoogleMapsApi';
import { MVT_1066 } from '../../flags/experiments';

import {
  updateLocationDetails,
  locationRestaurantSearch,
  locationBrandSearch,
  navigateToSavedAddress,
  hideLocationSearchModal,
  clearLocationQuery,
  changeHighlightedSuggestion,
  locationFromGeolocation,
  fetchAddressSuggestions,
  clearAddressSuggestions,
  selectAddressSuggestion,
  geocodeAddressFromString,
  showUndeliverableModal,
} from '../../actions/locationActions';
import {
  trackEnteredLocation,
  trackSearchLocationChange,
  trackGeolocationAvailable,
  trackGeolocationAttempt,
} from '../../actions/tracking/location';
import i18n from '../../utils/i18n';

export const MAXIMUM_SAVED_ADDRESSES = 4;

export class LocationSearchContainer extends Component {
  state = {
    hasGeolocation: false,
    isLocating: false,
    isLoading: false,
    isMapOpen: false,
    loadingAddressId: undefined,
    inputValue: '',
    isValidPostcode: undefined,
    errorMessage: undefined,
  };

  constructor(props) {
    super(props);

    this.state.inputValue = props.initialInputValue || '';
  }

  componentDidMount() {
    if ('geolocation' in navigator) {
      const { trackingPage: sourceView, isLoggedIn } = this.props;
      this.setState({ hasGeolocation: true });
      this.props.trackGeolocationAvailable({ sourceView, isLoggedIn });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      serviceError,
      formattedAddress,
      homepageSearchFilter,
      searchType,
      hasInputFocus,
    } = this.props;
    const {
      serviceError: prevServiceError,
      formattedAddress: prevFormattedAddress,
      hasInputFocus: prevHasInputFocus,
    } = prevProps;

    // If the area the user is searching for isn’t in our service area, trigger
    // display of the vote area modal.
    if (serviceError !== prevServiceError && serviceError === 'NOT_SERVICED') {
      this.props.showVoteAreaModal();
    }

    // If the LocationSearch is being used on a Brand Landing Page and the user
    // enters a location to which this brand cannot deliver, show the
    // undeliverable modal.
    if (
      searchType === 'brand' &&
      serviceError !== prevServiceError &&
      serviceError === 'UNDELIVERABLE'
    ) {
      this.props.showUndeliverableModal();
    }

    if (
      formattedAddress !== prevFormattedAddress &&
      formattedAddress !== '' &&
      formattedAddress !== undefined
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inputValue: formattedAddress });
    }

    // for homepage search filters, if the filters change, or if the
    // hasInputFocus prop is explicitly set, trigger `onFocusSearch()`
    if (
      (JSON.stringify(homepageSearchFilter) !== JSON.stringify(prevProps.homepageSearchFilter) &&
        homepageSearchFilter !== {}) ||
      (hasInputFocus !== prevHasInputFocus && hasInputFocus)
    ) {
      this.onFocusSearch();
    }
  }

  /**
   * Handles map marker drag events — we need to curry in the map instance since
   * that will be instantiated inside the child component.
   */
  onMapMarkerChange = (map) => (event) => {
    const position = {
      coords: {
        latitude: event.latLng.lat(),
        longitude: event.latLng.lng(),
      },
    };

    this.props
      .locationFromGeolocation({ position, source: 'map', sourceView: window.location.href })
      .then(() => {
        map.panTo(event.latLng);
        const { isLocationAccurate, formattedAddress, coords } = this.props;

        // this may now be redundant given that all map marker-chosen locations
        // are considered accurate

        /* eslint-disable-next-line no-console */
        console.log('onMapMarkerChange', {
          isLocationAccurate,
          formattedAddress,
          coords,
        });
      });
  };

  onRequestGeolocation = () => {
    const { tld, isLoggedIn, isPostcodeSearch, userAddresses, trackingPage } = this.props;

    this.setState({ isLocating: true });

    this.props.trackGeolocationAttempt({
      tld,
      sourceView: trackingPage,
      isPostcodeSearch,
      isLoggedIn,
      hasSavedAddresses: userAddresses.length > 0,
    });

    /**
     * Fires a locationFromGeolocation action to look these coordinates up using
     * the Google Maps API.
     */
    const successCallback = (position) => {
      this.props.locationFromGeolocation({ position, sourceView: window.location.href });
      this.setState({ isLocating: false });
    };

    /**
     * No matter what the cause of geolocation failing, we need to set
     * isLocating back to false, or else we get an endless spinner…
     */
    const failureCallback = () => {
      this.setState({ isLocating: false });
    };

    navigator.geolocation.getCurrentPosition(successCallback, failureCallback, {
      timeout: 30000, // this seems high, but we need to account for slower requests
    });
  };

  onFocus = () => once(this.props.trackEnteredLocation);

  onBlur = () => {
    const { onBlurSearch } = this.props;
    onBlurSearch();
  };

  onChange = (event) => {
    const { isPostcodeSearch } = this.props;
    const { isValidPostcode } = this.state;
    const { value: inputValue } = event.target;

    this.setState({ inputValue });

    switch (true) {
      case inputValue === '':
        this.props.clearLocationQuery();
        this.setState({ isValidPostcode: undefined, errorMessage: undefined });
        break;
      case isPostcodeSearch && isValidPostcode === false:
        this.validatePostcode();
        break;
      case !isPostcodeSearch:
        this.props.fetchAddressSuggestions({ searchString: inputValue });
        break;
      default:
        break;
    }
  };

  /**
   * Callback as part of a redirection event, i.e. either because the user has
   * entered a search term, or clicked a saved address.
   */
  navigateToRedirectUrl = () => {
    const {
      searchType,
      restaurantsRedirectLocation,
      menuRedirectLocation,
      hasClientSideRouting,
    } = this.props;
    let queryParams = {};

    const redirectLocation =
      searchType === 'restaurants' ? restaurantsRedirectLocation : menuRedirectLocation;

    if (redirectLocation) {
      const [pathname, search] = redirectLocation.split('?');

      // We slice to ignore the first slash
      const pathParts = pathname.slice(1).split('/');
      queryParams = {
        ...queryString.parse(search),
      };

      // extract and include these in the URL for restaurant list searches
      if (searchType === 'restaurants') {
        queryParams = {
          ...queryParams,
          city: pathParts[1],
          neighborhood: pathParts[2],
        };
      }

      // eslint-disable-next-line default-case
      switch (true) {
        case !hasClientSideRouting:
          window.location = redirectLocation;
          break;
        case searchType === 'restaurants':
          Router.push({ pathname: '/restaurants', query: queryParams }, redirectLocation);
          break;
        case searchType === 'brand':
          Router.push({ pathname: redirectLocation, query: queryParams });
          break;
      }
    }

    this.finishLoading({ isMapOpen: false });
    this.props.hideLocationSearchModal();
  };

  /**
   * Assuming an onFocusSearch prop was supplied, whenever the user changes
   * their filter selection (i.e. on the homepage), trigger that onFocusSearch
   * prop, then focus the input.
   */
  onFocusSearch() {
    this.props.onFocusSearch().then(() => {
      if (this.inputElement) this.inputElement.focus();
    });
  }

  /**
   * Event handler when the user clicks an existing, saved address.
   */
  onSavedAddressClick = ({ id, post_code: postcode, coordinates }) => {
    const { searchType, searchFilter: filter, brand } = this.props;
    this.setState({ loadingAddressId: id });
    this.trackSearchEvent({
      postcode,
      lat: coordinates[1],
      lng: coordinates[0],
    });

    this.props
      .navigateToSavedAddress({ id, searchType, filter, brand })
      .then(this.navigateToRedirectUrl);
  };

  /**
   * Proxy all key events whilst the LocationSearch TextInput is focused.
   */
  onKeyDown = (event) => {
    const { isPostcodeSearch, highlightedSuggestion, suggestions } = this.props;

    switch (event.key) {
      case 'Enter':
        event.preventDefault();

        if (!isPostcodeSearch && highlightedSuggestion) {
          const { placeId } = suggestions[highlightedSuggestion] || {};
          if (placeId) this.onSuggestionClick(placeId);
        } else {
          this.onSubmit(event);
        }
        break;
      case 'ArrowUp':
        if (!isPostcodeSearch) {
          event.preventDefault();
          if (highlightedSuggestion && highlightedSuggestion > 0) {
            const newSuggestion = highlightedSuggestion - 1;
            const { formattedAddress } = suggestions[newSuggestion] || {};

            this.props.changeHighlightedSuggestion({ newSuggestion });

            if (formattedAddress) {
              this.setState({
                inputValue: formattedAddress,
              });
            }
          }
        }
        break;
      case 'ArrowDown':
        if (!isPostcodeSearch) {
          event.preventDefault();
          if (
            highlightedSuggestion === undefined ||
            highlightedSuggestion < suggestions.length - 1
          ) {
            const newSuggestion =
              (highlightedSuggestion === undefined ? -1 : highlightedSuggestion) + 1;
            const { formattedAddress } = suggestions[newSuggestion] || {};

            this.props.changeHighlightedSuggestion({ newSuggestion });

            if (formattedAddress) {
              this.setState({
                inputValue: formattedAddress,
              });
            }
          }
        }
        break;
      default:
        break;
    }
  };

  /**
   * When a user clicks a suggestion, set their current address to this, clear
   * all the suggestions, and attempt to submit the form.
   */
  onSuggestionClick = (suggestionId) => {
    this.props.selectAddressSuggestion({ placeId: suggestionId }).then(() => {
      this.onSubmit();
    });
    this.props.clearAddressSuggestions();
    this.setState({ errorMessage: undefined });
  };

  /**
   * This is used to close the map modal for instances when LocationSearch isn’t
   * itself inside a modal.
   */
  onMapClose = () => {
    this.setState({ isMapOpen: false });
  };

  onModalClose = () => {
    const { initialInputValue } = this.props;
    const { isMapOpen } = this.state;
    let initialInputState = {};

    if (isMapOpen) {
      initialInputState = { inputValue: initialInputValue };
    }

    this.setState({ isMapOpen: false, ...initialInputState }, this.props.hideLocationSearchModal);
  };

  trackSearchEvent = ({ postcode, lat, lng }) => {
    const { trackingPage } = this.props;
    this.props.trackSearchLocationChange({
      sourceView: trackingPage,
      postCode: postcode,
      lat,
      lng,
    });
  };

  /**
   * A 500ms delay here means that the search button doesn’t just flicker
   * between isLoading and normal states.
   */
  finishLoading = (state, timeout = 500) => {
    window.setTimeout(() => {
      this.setState({ loadingAddressId: undefined, isLoading: false, ...state });
    }, timeout);
  };

  /**
   * Generate a location object to pass to the locationSearch action creator.
   */
  getLocationDetails = () => {
    const { isPostcodeSearch, placeId, formattedAddress, coords } = this.props;
    const { inputValue } = this.state;
    let location;

    if (isPostcodeSearch) {
      location = {
        post_code: inputValue,
        confirmed_on_map: false,
      };
    } else {
      const { lat, lng } = coords || {};

      location = {
        place_id: placeId,
        formatted_address: formattedAddress,
        // yes, this is the wrong order, for legacy reasons... :upside_down_smile:
        coordinates: [lng, lat],
        lat,
        lng,
      };
    }

    return location;
  };

  /**
   * Force a validation on the input value, displaying an error message if the
   * entered value is not a valid postcode.
   */
  validatePostcode = () => {
    const { tld } = this.props;
    const { inputValue } = this.state;
    const isValidPostcode = validatePostcode(inputValue, tld);

    this.setState({
      isValidPostcode,
      errorMessage:
        isValidPostcode === false
          ? i18n.gettext('locationSearch.error.invalid_postcode')
          : undefined,
    });

    if (!isValidPostcode) {
      /**
       * in case the postcode validation is triggered with the map modal open,
       * ensure the map is closed before focusing the input element.
       */
      this.setState({ isMapOpen: false });
      this.inputElement.focus();
    }

    return isValidPostcode;
  };

  /**
   * Validate the current address details in state, to see if they can be used
   * to query the Restaurants API endpoint.
   */
  validateAddress = () => {
    const { isPostcodeSearch, suggestions, coords, isLocationAccurate } = this.props;
    const { inputValue } = this.state;

    /**
     * Don’t validate an empty input.
     */
    if (!inputValue) {
      return false;
    }

    if (isPostcodeSearch) {
      const isValidPostcode = this.validatePostcode();

      /**
       * if they found their postcode using geolocation and it was a
       * high-quality geolocation response, they’re good.
       */
      if (isValidPostcode && coords && isLocationAccurate) {
        return true;
      }

      /**
       * If they found their postcode using geolocation but it *wasn’t*
       * considered accurate, get the user to double-check their location via
       * the map.
       */
      if (isValidPostcode && coords) {
        this.setState({ isMapOpen: true });
        return false;
      }

      /**
       * Otherwise we merely need to check for a valid postcode.
       */
      return isValidPostcode;
    }

    /**
     * If the user has been given multiple address suggestions, require that
     * they pick one.
     */
    if (suggestions.length > 1) {
      this.setState({
        errorMessage: i18n.gettext('locationSearch.error.autocomplete_not_selected'),
      });
      return false;
    }

    /**
     * If there is only one suggestion and the user-entered string matches it
     * perfectly, geocode *that* string and submit again.
     */
    if (suggestions.length === 1 && inputValue === suggestions[0].formattedAddress) {
      this.props.geocodeAddressFromString({ searchString: inputValue }).then(() => {
        this.props.clearAddressSuggestions();
        /**
         * We trigger submit again here since otherwise the user will have to
         * click the button twice: once to geocode their entered text, another
         * to search the restaurants API endpoint.
         */
        this.onSubmit();
      });

      return false;
    }

    /**
     * If an address selected from suggestions (or by direct text entry) isn’t
     * considered ‘accurate’ we can’t use it to make a Restaurants API search,
     * so we should prompt the user to refine their location from the modal
     * instead.
     */
    if (!isLocationAccurate && coords) {
      this.setState({ isMapOpen: true });
      return false;
    }

    /**
     * If we have a user-entered address that isn’t accurate enough to make a
     * determination from (and implicitly this means we don’t yet have any
     * coordinates), before prompting them to refine their map location we
     * should first geocode that partial address so as to ensure we have a set
     * of coordinates to display as the initial map viewport.
     */
    if (!isLocationAccurate) {
      this.props.geocodeAddressFromString({ searchString: inputValue }).then(() => {
        this.setState({ isMapOpen: true });
      });

      return false;
    }

    /**
     * Implicitly any other outcome is a valid address.
     */
    return true;
  };

  /**
   * Handle submit from clicking suggestions or either submit button (in card or
   * modal).
   */
  onSubmit = (event) => {
    if (event) event.preventDefault();

    const isAddressValid = this.validateAddress();

    if (isAddressValid) {
      const { searchType, searchFilter: filter, brand } = this.props;
      const location = this.getLocationDetails();

      this.trackSearchEvent(location);
      this.props.updateLocationDetails(location);
      this.setState({ isLoading: true });

      /**
       * Depending on what the search type is, we either want to redirect to a
       * restaurant list or a menu page.
       */

      // eslint-disable-next-line default-case
      switch (searchType) {
        case 'restaurants':
          this.props
            .locationRestaurantSearch({ location, filter })
            .then(this.navigateToRedirectUrl);
          break;
        case 'brand':
          this.props.locationBrandSearch({ location, brand }).then(this.navigateToRedirectUrl);
          break;
      }

      this.finishLoading({ isMapOpen: false }, 0);
    }
  };

  homepageSearchFilterMessage = ({ homepageSearchFilter, isPostcodeSearch }) =>
    i18n.gettext(
      `locationSearch.filterCaption.${homepageSearchFilter.tags ? 'collection' : 'restaurant'}.${
        isPostcodeSearch ? 'postcode' : 'address'
      }(%(name)s)`,
      { name: homepageSearchFilter.name },
    );

  renderLocationSearch = () => {
    const {
      currentUrl,
      isModal,
      caption,
      isPostcodeSearch,
      isLoggedIn,
      examplePostcode,
      userAddresses,
      homepageSearchFilter,
      suggestions,
      highlightedSuggestion,
      containerRef,
    } = this.props;
    const {
      hasGeolocation,
      isLocating,
      isLoading,
      loadingAddressId,
      inputValue,
      errorMessage,
    } = this.state;
    const hasSubmitButton = !isModal;
    const autoComplete = isPostcodeSearch ? 'postal-code' : 'off';
    const buttonText = hasSubmitButton
      ? i18n.gettext('locationSearch.searchButton.text')
      : undefined;

    const placeholder = isPostcodeSearch
      ? i18n.gettext('locationSearch.input.postcode.placeholder(%(postcode)s)', {
          postcode: examplePostcode,
        })
      : i18n.gettext('locationSearch.input.formattedAddress.placeholder');

    const homepageSearchFilterMessage = homepageSearchFilter.name
      ? this.homepageSearchFilterMessage({ isPostcodeSearch, homepageSearchFilter })
      : undefined;

    const loginMessageTemplate = isLoggedIn
      ? undefined
      : i18n.gettext('locationSearch.loginMessage(%(linkUrl)s)');
    const loginUrl = isLoggedIn ? undefined : `/login?redirect=${encodeURI(currentUrl)}`;

    return (
      <LocationSearch
        hasCard={!isModal}
        hasGeolocation={hasGeolocation}
        value={inputValue}
        inputRef={(element) => {
          this.inputElement = element;
        }}
        containerRef={containerRef}
        caption={
          caption ||
          i18n.gettext(
            `locationSearch.input.${isPostcodeSearch ? 'postcode' : 'formattedAddress'}.label`,
          )
        }
        placeholder={placeholder}
        errorMessage={errorMessage}
        searchFilterMessage={homepageSearchFilterMessage}
        loginMessageTemplate={loginMessageTemplate}
        loginUrl={loginUrl}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onKeyDown={this.onKeyDown}
        onLocateClick={this.onRequestGeolocation}
        onSubmit={this.onSubmit}
        buttonText={buttonText}
        autoComplete={autoComplete}
        savedAddressesCaption={i18n.gettext('locationSearch.savedAddresses.label')}
        savedAddressLoadingId={loadingAddressId}
        savedAddresses={userAddresses}
        onAddressClick={this.onSavedAddressClick}
        isLocating={isLocating}
        isLoading={isLoading}
        suggestions={suggestions}
        highlightedSuggestion={highlightedSuggestion}
        onSuggestionClick={this.onSuggestionClick}
      />
    );
  };

  renderModalFooter = () => {
    const { isLocationAccurate } = this.props;
    const { isLoading, isMapOpen } = this.state;

    return (
      <Button
        type="primary"
        onClick={this.onSubmit}
        isDisabled={isMapOpen && !isLocationAccurate}
        isLoading={isLoading}
        isBlock
      >
        {isMapOpen &&
          (isLocationAccurate
            ? i18n.gettext('locationSearch.mapModal.button.confirm')
            : i18n.gettext('locationSearch.mapModal.button.refine'))}
        {!isMapOpen && i18n.gettext('locationSearch.searchButton.text')}
      </Button>
    );
  };

  renderMap = () => {
    const { googleApiKey, coords } = this.props;
    const { inputValue } = this.state;

    return (
      <LocationMap
        label={i18n.gettext('locationSearch.mapModal.formattedAddress.labelText')}
        formattedAddress={inputValue}
        onMapMarkerChange={this.onMapMarkerChange}
        loadGoogleMapsApi={loadGoogleMapsApi}
        googleApiKey={googleApiKey}
        coords={coords}
      />
    );
  };

  render() {
    const { trackingPage, isModal, isModalOpen, searchType } = this.props;
    const { isMapOpen } = this.state;

    const locationModalProps = {
      appElement: '#__next',
      headerTitle: isModal
        ? i18n.gettext('locationSearch.modal.headerTitle')
        : i18n.gettext('locationSearch.mapModal.headerTitle'),
      hasOverflow: isModal && !isMapOpen,
      closeButtonLabel: i18n.gettext('closeButton.label'),
      renderFooter: this.renderModalFooter,
    };

    /**
     * There are two possible ways of rendering LocationSearch:
     *
     * 1. As a standalone component on the page (e.g. Home); in this scenario
     *    only the location refinement map appears within the modal.
     * 2. As an entirely modal-contained component (e.g. Restaurant Home); in
     *    this scenario the entire UI is within a modal, both for entering an
     *    address manually, using geolocation, picking from suggestions or
     *    refining from the map.
     */
    return (
      <Fragment>
        {!isModal && this.renderLocationSearch()}
        {!isModal && (
          <LocationModal {...locationModalProps} isOpen={isMapOpen} onClose={this.onMapClose}>
            {this.renderMap()}
          </LocationModal>
        )}
        {isModal && (
          <LocationModal {...locationModalProps} isOpen={isModalOpen} onClose={this.onModalClose}>
            {!isMapOpen && this.renderLocationSearch()}
            {isMapOpen && this.renderMap()}
          </LocationModal>
        )}
        <VoteAreaContainer page={trackingPage} />
        {searchType === 'brand' && <UndeliverableContainer />}
      </Fragment>
    );
  }
}

const SEARCH_FILTER_PROPS = {
  menu_group_uname: PropTypes.string, // eslint-disable-line camelcase
  name: PropTypes.string,
  tags: PropTypes.string,
};

LocationSearchContainer.propTypes = {
  googleApiKey: PropTypes.string.isRequired,
  currentUrl: PropTypes.string.isRequired,
  tld: PropTypes.string.isRequired,
  isModal: PropTypes.bool,
  formattedAddress: PropTypes.string,
  placeId: PropTypes.string,
  coords: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  isLocationAccurate: PropTypes.bool,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      placeId: PropTypes.string.isRequired,
      address: PropTypes.string.isRequired,
      locality: PropTypes.string,
    }),
  ),
  hasInputFocus: PropTypes.bool,
  highlightedSuggestion: PropTypes.number,
  trackingPage: PropTypes.string.isRequired,
  isPostcodeSearch: PropTypes.bool.isRequired,
  examplePostcode: PropTypes.string,
  isLoggedIn: PropTypes.bool.isRequired,
  hasClientSideRouting: PropTypes.bool,
  caption: PropTypes.string,
  userAddresses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      formattedAddress: PropTypes.string,
      postcode: PropTypes.string,
      coordinates: PropTypes.arrayOf(PropTypes.number).isRequired,
    }),
  ),
  updateLocationDetails: PropTypes.func.isRequired,
  searchType: PropTypes.oneOf(['restaurants', 'brand']),
  brand: isRequiredIf(PropTypes.string, ({ searchType }) => searchType === 'brand'),
  locationRestaurantSearch: PropTypes.func.isRequired,
  locationBrandSearch: PropTypes.func.isRequired,
  restaurantsRedirectLocation: isRequiredIf(
    PropTypes.string,
    ({ searchType }) => searchType === 'restaurants',
  ),
  menuRedirectLocation: isRequiredIf(PropTypes.string, ({ searchType }) => searchType === 'brand'),
  isModalOpen: PropTypes.bool,
  navigateToSavedAddress: PropTypes.func.isRequired,
  trackEnteredLocation: PropTypes.func.isRequired,
  trackSearchLocationChange: PropTypes.func.isRequired,
  trackGeolocationAvailable: PropTypes.func.isRequired,
  trackGeolocationAttempt: PropTypes.func.isRequired,
  hideLocationSearchModal: PropTypes.func.isRequired,
  clearLocationQuery: PropTypes.func.isRequired,
  changeHighlightedSuggestion: PropTypes.func.isRequired,
  locationFromGeolocation: PropTypes.func.isRequired,
  fetchAddressSuggestions: PropTypes.func.isRequired,
  clearAddressSuggestions: PropTypes.func.isRequired,
  selectAddressSuggestion: PropTypes.func.isRequired,
  geocodeAddressFromString: PropTypes.func.isRequired,
  showVoteAreaModal: PropTypes.func.isRequired,
  showUndeliverableModal: PropTypes.func.isRequired,
  initialInputValue: PropTypes.string,
  onFocusSearch: PropTypes.func,
  onBlurSearch: PropTypes.func,
  searchFilter: PropTypes.shape(SEARCH_FILTER_PROPS),
  homepageSearchFilter: PropTypes.shape(SEARCH_FILTER_PROPS),
  serviceError: PropTypes.string,
  containerRef: PropTypes.func,
};

LocationSearchContainer.defaultProps = {
  isModal: false,
  formattedAddress: undefined,
  placeId: undefined,
  coords: undefined,
  isLocationAccurate: undefined,
  suggestions: [],
  hasInputFocus: false,
  highlightedSuggestion: undefined,
  caption: undefined,
  examplePostcode: undefined,
  hasClientSideRouting: false,
  searchType: 'restaurants',
  brand: undefined,
  restaurantsRedirectLocation: undefined,
  menuRedirectLocation: undefined,
  isModalOpen: false,
  userAddresses: [],
  initialInputValue: undefined,
  onFocusSearch: () =>
    new Promise((resolve) => {
      resolve();
    }),
  onBlurSearch: () => undefined,
  searchFilter: undefined,
  homepageSearchFilter: {},
  serviceError: undefined,
  containerRef: undefined,
};

const mapStateToProps = (state) => {
  const { googleApiKey, currentUrl } = state.request;
  const { tld, postcodeSearch: isPostcodeSearch, examplePostcode } = state.request.country;
  const { locations: userAddresses } = state.location.recentAddresses;
  const { isLoggedIn } = state.user;
  const {
    formattedAddress,
    placeId,
    coords,
    postalCode,
    suggestions,
    highlightedSuggestion,
    isLocationAccurate,
  } = state.location.places;
  const {
    showModal: isModalOpen,
    restaurantsRedirectLocation,
    menuRedirectLocation,
  } = state.location.search;
  const hasClientSideRouting = hasVariant(state, MVT_1066, 'placeholders');
  const { neighborhood, postcode } = state.home.filters.location || {};
  const { menu_group_uname: filterMenuGroupUname, name: filterName, tags: filterTags } =
    state.homepageFeaturedContent.filter || {};
  const { error: serviceError } = state.location.search;

  return {
    googleApiKey,
    currentUrl,
    tld,
    isPostcodeSearch,
    examplePostcode,
    isLoggedIn,
    userAddresses: userAddresses.slice(0, MAXIMUM_SAVED_ADDRESSES),
    formattedAddress,
    placeId,
    coords,
    postalCode,
    isLocationAccurate,
    suggestions,
    highlightedSuggestion,
    restaurantsRedirectLocation,
    menuRedirectLocation,
    isModalOpen,
    hasClientSideRouting,
    initialInputValue: shortAddress({ tld, isPostcodeSearch, postcode, neighborhood }),
    homepageSearchFilter: {
      menu_group_uname: filterMenuGroupUname, // eslint-disable camelcase
      name: filterName,
      tags: filterTags,
    },
    serviceError: (serviceError && serviceError.type) || undefined,
  };
};
const mapDispatchToProps = (dispatch) => ({
  updateLocationDetails: (location) => dispatch(updateLocationDetails(location)),
  locationRestaurantSearch: (options) => dispatch(locationRestaurantSearch(options)),
  locationBrandSearch: (options) => dispatch(locationBrandSearch(options)),
  navigateToSavedAddress: (options) => dispatch(navigateToSavedAddress(options)),
  trackEnteredLocation: () => dispatch(trackEnteredLocation()),
  trackSearchLocationChange: (options) => dispatch(trackSearchLocationChange(options)),
  trackGeolocationAvailable: (options) => dispatch(trackGeolocationAvailable(options)),
  trackGeolocationAttempt: (options) => dispatch(trackGeolocationAttempt(options)),
  hideLocationSearchModal: () => dispatch(hideLocationSearchModal()),
  clearLocationQuery: () => dispatch(clearLocationQuery()),
  changeHighlightedSuggestion: ({ newSuggestion }) =>
    dispatch(changeHighlightedSuggestion({ newSuggestion })),
  locationFromGeolocation: (position) => dispatch(locationFromGeolocation(position)),
  fetchAddressSuggestions: (options) => dispatch(fetchAddressSuggestions(options)),
  clearAddressSuggestions: () => dispatch(clearAddressSuggestions()),
  selectAddressSuggestion: (options) => dispatch(selectAddressSuggestion(options)),
  geocodeAddressFromString: (options) => dispatch(geocodeAddressFromString(options)),
  showVoteAreaModal: () => dispatch(showVoteAreaModal()),
  showUndeliverableModal: () => dispatch(showUndeliverableModal()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(LocationSearchContainer);
